import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';

interface FormData {
  // Your Details
  email: string;
  first_name: string;
  last_name: string;
  employer_name: string;
  employer_address: string;

  // Employee Details
  employee_name: string;
  employee_address: string;

  // Employment Terms
  start_date: string;
  probation_period: string;
  work_address: string;

  // Work Schedule
  weekly_hours: string;
  working_time_opt_out?: boolean;
  work_start_time: string;
  work_end_time: string;
  work_days: string;
  children_names?: string;
  children_ages?: string;

  // Compensation
  salary_amount: string;
  payment_date: string;
  payment_method: string;
  overtime_rate: string;

  // Leave & Notice
  annual_leave_weeks: string;
  bank_holidays_included: string;
  notice_period: string;
  additional_duties?: string;
}

interface ContractVariablesFormProps {
  onSubmit: (data: FormData) => void;
  isSubmitting?: boolean;
}

const STEPS = [
  { id: 1, title: 'Your Details', description: 'Information about you (the employer)' },
  { id: 2, title: 'Employee Details', description: 'Information about your nanny' },
  { id: 3, title: 'Employment Terms', description: 'Start date and work location' },
  { id: 4, title: 'Work Schedule', description: 'Hours and days of work' },
  { id: 5, title: 'Compensation', description: 'Salary and payment details' },
  { id: 6, title: 'Leave & Notice', description: 'Holiday and notice periods' },
  { id: 7, title: 'Review', description: 'Check your details before submitting' },
];

export const ContractVariablesForm: React.FC<ContractVariablesFormProps> = ({
  onSubmit,
  isSubmitting = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    first_name: '',
    last_name: '',
    employer_name: '',
    employer_address: '',
    employee_name: '',
    employee_address: '',
    start_date: '',
    probation_period: '3',
    work_address: '',
    weekly_hours: '',
    working_time_opt_out: false,
    work_start_time: '',
    work_end_time: '',
    work_days: '',
    children_names: '',
    children_ages: '',
    salary_amount: '',
    payment_date: '',
    payment_method: 'bank transfer',
    overtime_rate: '1.5',
    annual_leave_weeks: '5.6',
    bank_holidays_included: 'included',
    notice_period: '4 weeks',
    additional_duties: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [workAddressSameAsEmployer, setWorkAddressSameAsEmployer] = useState(false);

  // Helper function to format date in British format (DD/MM/YYYY)
  const formatDateToBritish = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to validate and format date input as user types
  const handleDateInput = (value: string): void => {
    // Remove any non-numeric characters except /
    let cleaned = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes as user types
    if (cleaned.length >= 2 && cleaned.charAt(2) !== '/') {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    }
    if (cleaned.length >= 5 && cleaned.charAt(5) !== '/') {
      cleaned = cleaned.slice(0, 5) + '/' + cleaned.slice(5);
    }
    
    // Limit to DD/MM/YYYY format length
    if (cleaned.length > 10) {
      cleaned = cleaned.slice(0, 10);
    }
    
    updateField('start_date', cleaned);
  };

  // Helper function to validate DD/MM/YYYY date
  const isValidDate = (dateString: string): boolean => {
    if (!dateString) return false;
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(regex);
    if (!match) return false;
    
    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    
    // Check if date is valid (e.g., no Feb 31st)
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && 
           date.getMonth() === month - 1 && 
           date.getDate() === day;
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleWorkAddressCheckbox = (checked: boolean) => {
    setWorkAddressSameAsEmployer(checked);
    if (checked) {
      updateField('work_address', formData.employer_address);
    } else {
      updateField('work_address', '');
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    switch (step) {
      case 1: // Your Details
        if (!formData.email) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Invalid email format';
        }
        if (!formData.first_name) newErrors.first_name = 'First name is required';
        if (!formData.last_name) newErrors.last_name = 'Last name is required';
        if (!formData.employer_name) newErrors.employer_name = 'Employer name is required';
        if (!formData.employer_address) newErrors.employer_address = 'Employer address is required';
        break;

      case 2: // Employee Details
        if (!formData.employee_name) newErrors.employee_name = 'Employee name is required';
        if (!formData.employee_address) newErrors.employee_address = 'Employee address is required';
        break;

      case 3: // Employment Terms
        if (!formData.start_date) {
          newErrors.start_date = 'Start date is required';
        } else if (!isValidDate(formData.start_date)) {
          newErrors.start_date = 'Please enter a valid date in DD/MM/YYYY format';
        }
        if (!formData.work_address) newErrors.work_address = 'Work address is required';
        break;

      case 4: // Work Schedule
        if (!formData.weekly_hours) newErrors.weekly_hours = 'Weekly hours is required';
        else if (parseFloat(formData.weekly_hours) < 1) {
          newErrors.weekly_hours = 'Hours must be at least 1';
        } else if (parseFloat(formData.weekly_hours) > 48 && !formData.working_time_opt_out) {
          newErrors.weekly_hours = 'Hours over 48 require Working Time Directive opt-out';
        }
        if (!formData.work_start_time) newErrors.work_start_time = 'Start time is required';
        if (!formData.work_end_time) newErrors.work_end_time = 'End time is required';
        if (!formData.work_days) newErrors.work_days = 'Work days are required';
        break;

      case 5: // Compensation
        if (!formData.salary_amount) newErrors.salary_amount = 'Salary is required';
        else if (parseFloat(formData.salary_amount) <= 0) {
          newErrors.salary_amount = 'Salary must be greater than 0';
        }
        if (!formData.payment_date) newErrors.payment_date = 'Payment date is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    // Validate all steps before submission
    let allValid = true;
    for (let i = 1; i <= 6; i++) {
      if (!validateStep(i)) {
        allValid = false;
        setCurrentStep(i);
        break;
      }
    }

    if (allValid) {
      onSubmit(formData);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Your Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="you@example.com"
              />
              {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Your First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  placeholder="John"
                />
                {errors.first_name && <p className="text-sm text-red-500 mt-1">{errors.first_name}</p>}
              </div>
              <div>
                <Label htmlFor="last_name">Your Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  placeholder="Smith"
                />
                {errors.last_name && <p className="text-sm text-red-500 mt-1">{errors.last_name}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="employer_name">Employer Name *</Label>
              <Input
                id="employer_name"
                value={formData.employer_name}
                onChange={(e) => updateField('employer_name', e.target.value)}
                placeholder="e.g., John & Jane Smith"
              />
              {errors.employer_name && <p className="text-sm text-red-500 mt-1">{errors.employer_name}</p>}
              <p className="text-sm text-gray-500 mt-1">
                This can be your full name or both parents' names
              </p>
            </div>
            <div>
              <Label htmlFor="employer_address">Employer Address *</Label>
              <Textarea
                id="employer_address"
                value={formData.employer_address}
                onChange={(e) => updateField('employer_address', e.target.value)}
                placeholder="123 Main Street, London, SW1A 1AA"
                rows={3}
              />
              {errors.employer_address && <p className="text-sm text-red-500 mt-1">{errors.employer_address}</p>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="employee_name">Nanny's Full Name *</Label>
              <Input
                id="employee_name"
                value={formData.employee_name}
                onChange={(e) => updateField('employee_name', e.target.value)}
                placeholder="e.g., Mary Johnson"
              />
              {errors.employee_name && <p className="text-sm text-red-500 mt-1">{errors.employee_name}</p>}
            </div>
            <div>
              <Label htmlFor="employee_address">Nanny's Home Address *</Label>
              <Textarea
                id="employee_address"
                value={formData.employee_address}
                onChange={(e) => updateField('employee_address', e.target.value)}
                placeholder="456 Oak Avenue, London, E1 6AN"
                rows={3}
              />
              {errors.employee_address && <p className="text-sm text-red-500 mt-1">{errors.employee_address}</p>}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="start_date">Employment Start Date (DD/MM/YYYY) *</Label>
              <Input
                id="start_date"
                type="text"
                value={formData.start_date}
                onChange={(e) => handleDateInput(e.target.value)}
                placeholder="DD/MM/YYYY"
                maxLength={10}
              />
              {errors.start_date && <p className="text-sm text-red-500 mt-1">{errors.start_date}</p>}
              <p className="text-sm text-gray-500 mt-1">
                Enter date in British format (day/month/year)
              </p>
            </div>
            <div>
              <Label htmlFor="probation_period">Probation Period (months)</Label>
              <Select
                value={formData.probation_period}
                onValueChange={(value) => updateField('probation_period', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 month</SelectItem>
                  <SelectItem value="2">2 months</SelectItem>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                  <SelectItem value="12">12 months</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">Typical is 3 months</p>
            </div>
            <div>
              <Label htmlFor="work_address">Work Location *</Label>
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox
                  id="work_address_same"
                  checked={workAddressSameAsEmployer}
                  onCheckedChange={handleWorkAddressCheckbox}
                />
                <Label
                  htmlFor="work_address_same"
                  className="text-sm font-normal cursor-pointer"
                >
                  Same as employer address
                </Label>
              </div>
              <Textarea
                id="work_address"
                value={formData.work_address}
                onChange={(e) => {
                  updateField('work_address', e.target.value);
                  if (workAddressSameAsEmployer) {
                    setWorkAddressSameAsEmployer(false);
                  }
                }}
                placeholder="123 Main Street, London, SW1A 1AA"
                rows={3}
                disabled={workAddressSameAsEmployer}
              />
              {errors.work_address && <p className="text-sm text-red-500 mt-1">{errors.work_address}</p>}
              <p className="text-sm text-gray-500 mt-1">
                Usually your home address
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="weekly_hours">Weekly Hours *</Label>
              <Input
                id="weekly_hours"
                type="number"
                min="1"
                value={formData.weekly_hours}
                onChange={(e) => updateField('weekly_hours', e.target.value)}
                placeholder="40"
              />
              {errors.weekly_hours && <p className="text-sm text-red-500 mt-1">{errors.weekly_hours}</p>}
              <p className="text-sm text-gray-500 mt-1">
                Maximum 48 hours per week under UK law (unless opted out below)
              </p>
              {parseFloat(formData.weekly_hours) > 48 && (
                <div className="flex items-start space-x-2 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <Checkbox
                    id="working_time_opt_out"
                    checked={formData.working_time_opt_out || false}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, working_time_opt_out: checked as boolean }))
                    }
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="working_time_opt_out"
                      className="text-sm font-normal cursor-pointer"
                    >
                      <strong>Working Time Directive Opt-Out:</strong> The employee agrees to opt out of the 48-hour maximum working week as permitted under the Working Time Regulations 1998.
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      This is required for working more than 48 hours per week. The employee can withdraw this opt-out with notice.
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="work_start_time">Start Time *</Label>
                <Input
                  id="work_start_time"
                  type="time"
                  value={formData.work_start_time}
                  onChange={(e) => updateField('work_start_time', e.target.value)}
                />
                {errors.work_start_time && <p className="text-sm text-red-500 mt-1">{errors.work_start_time}</p>}
              </div>
              <div>
                <Label htmlFor="work_end_time">End Time *</Label>
                <Input
                  id="work_end_time"
                  type="time"
                  value={formData.work_end_time}
                  onChange={(e) => updateField('work_end_time', e.target.value)}
                />
                {errors.work_end_time && <p className="text-sm text-red-500 mt-1">{errors.work_end_time}</p>}
              </div>
            </div>
            <div>
              <Label htmlFor="work_days">Working Days *</Label>
              <Input
                id="work_days"
                value={formData.work_days}
                onChange={(e) => updateField('work_days', e.target.value)}
                placeholder="e.g., Monday to Friday"
              />
              {errors.work_days && <p className="text-sm text-red-500 mt-1">{errors.work_days}</p>}
            </div>
            <div>
              <Label htmlFor="children_names">Children's Names (optional)</Label>
              <Input
                id="children_names"
                value={formData.children_names}
                onChange={(e) => updateField('children_names', e.target.value)}
                placeholder="e.g., Emma and Oliver"
              />
            </div>
            {formData.children_names && (
              <div>
                <Label htmlFor="children_ages">Children's Ages</Label>
                <Input
                  id="children_ages"
                  value={formData.children_ages}
                  onChange={(e) => updateField('children_ages', e.target.value)}
                  placeholder="e.g., 3 and 5 years old"
                />
              </div>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="salary_amount">Annual Gross Salary (£) *</Label>
              <Input
                id="salary_amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.salary_amount}
                onChange={(e) => updateField('salary_amount', e.target.value)}
                placeholder="25000"
              />
              {errors.salary_amount && <p className="text-sm text-red-500 mt-1">{errors.salary_amount}</p>}
            </div>
            <div>
              <Label htmlFor="payment_date">Payment Date *</Label>
              <Input
                id="payment_date"
                value={formData.payment_date}
                onChange={(e) => updateField('payment_date', e.target.value)}
                placeholder="e.g., Last working day of the month"
              />
              {errors.payment_date && <p className="text-sm text-red-500 mt-1">{errors.payment_date}</p>}
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(value) => updateField('payment_method', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="overtime_rate">Overtime Rate Multiplier</Label>
              <Input
                id="overtime_rate"
                type="number"
                min="1.0"
                step="0.1"
                value={formData.overtime_rate}
                onChange={(e) => updateField('overtime_rate', e.target.value)}
              />
              <p className="text-sm text-gray-500 mt-1">
                Typical is 1.5x (time and a half)
              </p>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="annual_leave_weeks">Annual Leave (weeks)</Label>
              <Input
                id="annual_leave_weeks"
                type="number"
                min="5.6"
                step="0.1"
                value={formData.annual_leave_weeks}
                onChange={(e) => updateField('annual_leave_weeks', e.target.value)}
              />
              <p className="text-sm text-gray-500 mt-1">
                UK statutory minimum is 5.6 weeks
              </p>
            </div>
            <div>
              <Label htmlFor="bank_holidays_included">Bank Holidays</Label>
              <Select
                value={formData.bank_holidays_included}
                onValueChange={(value) => updateField('bank_holidays_included', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="included">Included in annual leave</SelectItem>
                  <SelectItem value="not included">Not included (additional days off)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="notice_period">Notice Period</Label>
              <Select
                value={formData.notice_period}
                onValueChange={(value) => updateField('notice_period', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1 week">1 week</SelectItem>
                  <SelectItem value="2 weeks">2 weeks</SelectItem>
                  <SelectItem value="4 weeks">4 weeks (1 month)</SelectItem>
                  <SelectItem value="8 weeks">8 weeks (2 months)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500 mt-1">
                Applies to both employer and employee
              </p>
            </div>
            <div>
              <Label htmlFor="additional_duties">Additional Duties (optional)</Label>
              <Textarea
                id="additional_duties"
                value={formData.additional_duties}
                onChange={(e) => updateField('additional_duties', e.target.value)}
                placeholder="e.g., Light housekeeping, meal preparation for children, school pick-ups"
                rows={4}
              />
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Review Your Details</h3>
              <p className="text-sm text-blue-800">
                Please review all the information below. You can go back to edit any section.
              </p>
            </div>

            <div className="space-y-4">
              <ReviewSection title="Your Details">
                <ReviewItem label="Email" value={formData.email} />
                <ReviewItem label="Name" value={`${formData.first_name} ${formData.last_name}`} />
                <ReviewItem label="Employer Name" value={formData.employer_name} />
                <ReviewItem label="Employer Address" value={formData.employer_address} />
              </ReviewSection>

              <ReviewSection title="Employee Details">
                <ReviewItem label="Name" value={formData.employee_name} />
                <ReviewItem label="Address" value={formData.employee_address} />
              </ReviewSection>

              <ReviewSection title="Employment Terms">
                <ReviewItem label="Start Date" value={formData.start_date} />
                <ReviewItem label="Probation Period" value={`${formData.probation_period} months`} />
                <ReviewItem label="Work Location" value={formData.work_address} />
              </ReviewSection>

              <ReviewSection title="Work Schedule">
                <ReviewItem label="Weekly Hours" value={formData.weekly_hours} />
                {formData.working_time_opt_out && (
                  <ReviewItem label="Working Time Opt-Out" value="Yes - Employee has opted out of 48-hour maximum" />
                )}
                <ReviewItem label="Work Hours" value={`${formData.work_start_time} - ${formData.work_end_time}`} />
                <ReviewItem label="Work Days" value={formData.work_days} />
                {formData.children_names && (
                  <>
                    <ReviewItem label="Children" value={formData.children_names} />
                    {formData.children_ages && <ReviewItem label="Ages" value={formData.children_ages} />}
                  </>
                )}
              </ReviewSection>

              <ReviewSection title="Compensation">
                <ReviewItem label="Annual Salary" value={`£${formData.salary_amount}`} />
                <ReviewItem label="Payment Date" value={formData.payment_date} />
                <ReviewItem label="Payment Method" value={formData.payment_method} />
                <ReviewItem label="Overtime Rate" value={`${formData.overtime_rate}x`} />
              </ReviewSection>

              <ReviewSection title="Leave & Notice">
                <ReviewItem label="Annual Leave" value={`${formData.annual_leave_weeks} weeks`} />
                <ReviewItem label="Bank Holidays" value={formData.bank_holidays_included} />
                <ReviewItem label="Notice Period" value={formData.notice_period} />
                {formData.additional_duties && (
                  <ReviewItem label="Additional Duties" value={formData.additional_duties} />
                )}
              </ReviewSection>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  step.id < currentStep
                    ? 'bg-green-500 text-white'
                    : step.id === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step.id < currentStep ? <Check className="w-5 h-5" /> : step.id}
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-12 h-1 mx-2 ${
                    step.id < currentStep ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <p className="text-sm text-gray-600 text-center mt-2">
          Step {currentStep} of {STEPS.length}
        </p>
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle>{STEPS[currentStep - 1].title}</CardTitle>
          <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
        </CardHeader>
        <CardContent>
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {currentStep < STEPS.length ? (
              <Button onClick={handleNext}>
                Next
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit & Get Editor Link'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper components for review step
const ReviewSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
    <div className="space-y-2">{children}</div>
  </div>
);

const ReviewItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-600">{label}:</span>
    <span className="text-gray-900 font-medium text-right">{value}</span>
  </div>
);

